import { creditCardSurchargeStatement } from "helpers/fee";
import { centsToDollars, parsePrice } from "helpers/price";
import styled from "styled-components";

// CLE-2798 — the credit-card surcharge, laid out like the new checkout design (v0 "Secure checkout"):
// a "Payment method fees" box shown before the card is entered, and amount / surcharge / total rows
// once Finix has told us what kind of card it is: a notice, then the rows in a box, under the card
// fields. Finix only reports the card type once the card is tokenized, so the checkout reads the card
// in the background when its fields are complete.

const StyledFeesBox = styled.div`
  /* Matches the v0 checkout: light neutral card, dark semibold title, gray body text. */
  background-color: #fafafa;
  border: 1px solid #e4e4e7;
  border-radius: 16px;
  padding: 16px 20px;
  text-align: left;

  /* The checkout container styles every <p> centered, bold, 24px and purple, so each paragraph here
     sets all of those itself. */
  .fees-title {
    color: #18181b;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    text-align: left;
    margin: 0 0 4px;
  }

  .fees-text {
    color: #71717a;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    text-align: left;
    margin: 0;
  }
`;

const StyledSummary = styled.div`
  /* v0: a light neutral box under the notice, the same family as the fees box. */
  background-color: #fafafa;
  border: 1px solid #e4e4e7;
  border-radius: 16px;
  margin-top: 12px;
  padding: 12px 20px;
  text-align: left;

  .summary-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    color: #475467;
    font-size: 14px;
    line-height: 20px;
    padding: 4px 0;
  }

  .summary-caption {
    color: #667085;
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
    text-align: left;
    margin: 0 0 4px;
  }

  .summary-total {
    border-top: 1px solid #eaecf0;
    margin-top: 8px;
    padding-top: 10px;
    color: #101828;
    font-weight: 600;
  }

  .no-fee {
    color: #067647;
    font-weight: 600;
  }
`;

export function paymentMethodFeesText(
  percent: number,
  isPaymentPlan: boolean
): string {
  const base = creditCardSurchargeStatement(percent);
  return isPaymentPlan
    ? `${base} On a payment plan it also applies to each installment charged to a credit card.`
    : base;
}

/** Shown before the card is entered (card-brand rule: disclose the surcharge up front). */
export function PaymentMethodFeesNotice({
  percent,
  isPaymentPlan,
}: {
  percent: number;
  isPaymentPlan: boolean;
}) {
  if (!(percent > 0)) return null;
  return (
    <StyledFeesBox data-testid="credit-card-fee-notice">
      <p className="fees-title">Payment method fees</p>
      <p className="fees-text">
        {paymentMethodFeesText(percent, isPaymentPlan)}
      </p>
    </StyledFeesBox>
  );
}

const StyledSurchargeAlert = styled.div`
  /* v0 (2026-09-17): amber, not red. */
  background-color: #fef3e2;
  border: 1px solid #f5c77e;
  border-radius: 12px;
  padding: 12px 16px;
  margin-top: 12px;
  text-align: left;

  /* The checkout container styles every <p> centered, bold, 24px and purple. */
  p {
    color: #93520a;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    text-align: left;
    margin: 0;
  }
`;

/** Ani (2026-09-16): once the card is read as credit, say the fee was added and how to avoid it. */
export function CreditCardSurchargeAlert({ percent }: { percent: number }) {
  if (!(percent > 0)) return null;
  return (
    <StyledSurchargeAlert
      role="alert"
      data-testid="credit-card-surcharge-alert"
    >
      <p>
        A {percent}% credit card surcharge has been added. To avoid this fee,
        use a debit or HSA/FSA card.
      </p>
    </StyledSurchargeAlert>
  );
}

export type PaymentFeeSummaryState =
  | {
      kind: "surcharge";
      amountInCents: number;
      surchargeInCents: number;
      percent: number;
      /** Two-card payments: one line per card under the surcharge row. */
      perCardLines?: string[];
    }
  | { kind: "noFee" };

/** Amount / surcharge / total once the card type is known; "No payment fee" for a debit card. */
export function PaymentFeeSummary({
  state,
}: {
  state: PaymentFeeSummaryState | null;
}) {
  if (!state) return null;

  if (state.kind === "noFee") {
    return (
      <StyledSummary data-testid="credit-card-fee-summary">
        <div className="summary-row">
          <span>Payment fee</span>
          <span className="no-fee">No payment fee</span>
        </div>
      </StyledSummary>
    );
  }

  const { amountInCents, surchargeInCents, percent, perCardLines } = state;
  return (
    <StyledSummary data-testid="credit-card-fee-summary">
      <div className="summary-row">
        <span>Payment amount</span>
        <span>{parsePrice(centsToDollars(amountInCents))}</span>
      </div>
      <div className="summary-row">
        <span>Credit card surcharge ({percent}%)</span>
        <span>{parsePrice(centsToDollars(surchargeInCents))}</span>
      </div>
      {perCardLines?.map((line) => (
        <p key={line} className="summary-caption">
          {line}
        </p>
      ))}
      <div className="summary-row summary-total">
        <span>Total payment</span>
        <span>
          {parsePrice(centsToDollars(amountInCents + surchargeInCents))}
        </span>
      </div>
    </StyledSummary>
  );
}

/** Same formula as the backend (`computeGfeCreditCardFeeInCents`): to the cent, never rounded up. */
export function creditCardFeeForAmount(
  percent: number,
  amountInCents: number
): number {
  if (!(percent > 0) || !(amountInCents > 0)) return 0;
  return Math.round((amountInCents * percent) / 100);
}

/** What the background card read found: credit (surcharge applies) or not, and at what rate. */
export interface ICardFeePreview {
  feeApplies: boolean;
  feePercent: number;
}

/**
 * The card is read again when the patient presses Pay: Finix never shows us the field values, so the
 * background read cannot prove the card was not edited since. When the read at Pay agrees with what was
 * on screen (same credit/debit answer, same fee) the patient already saw that total, so it is charged
 * at once. Otherwise (nothing shown yet, or the card changed) the confirmation step shows the total.
 */
export function quoteMatchesPreview(
  preview: ICardFeePreview | null,
  quote: { feeApplies: boolean; feeAmountInCents: number },
  amountInCents: number
): boolean {
  if (!preview || preview.feeApplies !== quote.feeApplies) return false;
  if (!quote.feeApplies) return true;
  return (
    Math.abs(
      creditCardFeeForAmount(preview.feePercent, amountInCents) -
        quote.feeAmountInCents
    ) <= 1
  );
}

/** Two cards: one click only when EACH card's fee from the holds is the one already on screen. */
export function splitFeesMatchPreview(
  previews: [ICardFeePreview | null, ICardFeePreview | null],
  feesInCents: [number, number],
  amountsInCents: [number, number]
): boolean {
  return previews.every((preview, i) =>
    quoteMatchesPreview(
      preview,
      { feeApplies: feesInCents[i] > 0, feeAmountInCents: feesInCents[i] },
      amountsInCents[i]
    )
  );
}
